// Root travel for a generated clip on the part rig: it binds bone ROTATIONS only, so the
// clip walks on the spot and the avatar root travels. Placed by the render loop at the
// clip's own progress — one clock for legs and travel.
// Notes: ~/comments/aos-threejs-poc/generatedRootMotion_createRootMotion_render_loop_clock.md

/** Frame index and blend weight for normalised time `t01` across `frames`. */
function frameAt(frames, t01) {
  const clamped = t01 <= 0 ? 0 : (t01 >= 1 ? 1 : t01)
  const position = clamped * (frames - 1)
  const index = Math.min(frames - 2, Math.floor(position))
  return [index, position - index]
}

/** [x, z] travel from frame 0 at normalised time `t01`, along the generator's own path. */
export function rootOffsetAt(rootPath, frames, t01) {
  if (!rootPath || frames < 2) return [0, 0]
  const [index, blend] = frameAt(frames, t01)
  const a = index * 3
  const b = (index + 1) * 3
  return [
    lerp(rootPath[a] - rootPath[0], rootPath[b] - rootPath[0], blend),
    lerp(rootPath[a + 2] - rootPath[2], rootPath[b + 2] - rootPath[2], blend),
  ]
}

/** Net turn from frame 0 at `t01`, off the generator's own per-frame yaw series. */
export function yawOffsetAt(yawPath, frames, t01) {
  if (!yawPath || frames < 2) return 0
  const [index, blend] = frameAt(frames, t01)
  return lerp(yawPath[index], yawPath[index + 1], blend)
}

/** The same reading for a clip that states only its total travel. */
export function straightOffsetAt(travel, t01) {
  if (!travel) return [0, 0]
  const clamped = t01 <= 0 ? 0 : (t01 >= 1 ? 1 : t01)
  return [travel[0] * clamped, travel[1] * clamped]
}

/** Whether a travel delta is worth driving at all. */
export function hasTravel(travel) {
  return !!travel && (Math.abs(travel[0]) > 1e-3 || Math.abs(travel[1]) > 1e-3)
}

/** Root travel for `group` along a generated clip, in the frame the clip was requested in.
 *
 *  @returns {{placeAt, started, finish, cancel}} `placeAt(t01)` is the render loop's call at
 *    the clip's progress; `finish()` lands the full travel and commits the yaw. */
export function createRootMotion({ group, travel, yaw = 0, rootPath = null, yawPath = null, frames = 0 }) {
  const origin = { x: group.position.x, z: group.position.z, yaw: group.rotation.y }
  // The request is in the character's frame, so travel turns with the root (as applyRootDrive).
  const cos = Math.cos(origin.yaw)
  const sin = Math.sin(origin.yaw)
  const offsetAt = rootPath && frames >= 2
    ? (t) => rootOffsetAt(rootPath, frames, t)
    : (t) => straightOffsetAt(travel, t)
  // A yaw series means the pose no longer carries the turn, so the root turns as it walks.
  const turnsWhileWalking = !!(yawPath && frames >= 2)
  let started = false
  let live = true

  const place = (t01) => {
    const [dx, dz] = offsetAt(t01)
    group.position.x = origin.x + dx * cos + dz * sin
    group.position.z = origin.z - dx * sin + dz * cos
    if (turnsWhileWalking) group.rotation.y = origin.yaw + yawOffsetAt(yawPath, frames, t01)
  }

  return {
    /** The clip was posed at `t01` — put the root where it has walked to. */
    placeAt(t01) {
      if (!live) return
      started = true
      place(t01)
    },
    /** Whether the clip has been posed at all. */
    get started() { return started },
    /** Land the travel and commit the yaw. A clip never posed commits nothing — a teleport is not a walk. */
    finish() {
      if (!live) return
      live = false
      if (!started) return
      place(1)
      // Only when the pose still holds the turn: released, it would snap back otherwise.
      if (!turnsWhileWalking && yaw) group.rotation.y += yaw
    },
    cancel() { live = false },
  }
}

function lerp(a, b, t) { return a + (b - a) * t }
